import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../hooks/useAuth';
import { usePair } from '../hooks/usePair';
import { useModuleProgress } from '../hooks/useModuleProgress';
import { useSharedNotes } from '../hooks/useSharedNotes';

const {
  FiBook, FiMessageCircle, FiHeart, FiTarget,
  FiChevronLeft, FiChevronRight, FiSave, FiEdit3,
  FiCheck, FiCheckCircle, FiAlertCircle, FiClock, FiLoader,
} = FiIcons;

const WEEK_TITLES = {
  1: 'Jesus is Your Salvation and Story',
  2: 'You Are A New Creation',
  3: 'The Holy Spirit Points You To Jesus',
  4: 'Surrender Your Struggles',
  5: 'You Are Called To Give Generously',
  6: 'Follow Jesus By Serving Others',
};

const SCRIPTURE_TEXTS = {
  1: {
    scripture1: {
      reference: 'John 3:16-19',
      nlt: 'For this is how God loved the world: He gave his one and only Son, so that everyone who believes in him will not perish but have eternal life. God sent his Son into the world not to judge the world, but to save the world through him. There is no judgment against anyone who believes in him. But anyone who does not believe in him has already been judged for not believing in God\'s one and only Son. And the judgment is based on this fact: God\'s light came into the world, but people loved the darkness more than the light, for their actions were evil.',
      esv: 'For God so loved the world, that he gave his only Son, that whoever believes in him should not perish but have eternal life. For God did not send his Son into the world to condemn the world, but in order that the world might be saved through him. Whoever believes in him is not condemned, but whoever does not believe is condemned already, because he has not believed in the name of the only Son of God. And this is the judgment: the light has come into the world, and people loved the darkness rather than the light because their works were evil.'
    },
    scripture2: {
      reference: '1 Timothy 1:15-16',
      nlt: 'This is a trustworthy saying, and everyone should accept it: "Christ Jesus came into the world to save sinners"—and I am the worst of them all. But God had mercy on me so that Christ Jesus could use me as a prime example of his great patience with even the worst sinners. Then others will realize that they, too, can believe in him and receive eternal life.',
      esv: 'The saying is trustworthy and deserving of full acceptance, that Christ Jesus came into the world to save sinners, of whom I am the foremost. But I received mercy for this reason, that in me, as the foremost, Jesus Christ might display his perfect patience as an example to those who were to believe in him for eternal life.'
    },
  },
  2: {
    scripture1: {
      reference: 'Galatians 2:20',
      nlt: 'My old self has been crucified with Christ. It is no longer I who live, but Christ lives in me. So I live in this earthly body by trusting in the Son of God, who loved me and gave himself for me.',
      esv: 'I have been crucified with Christ. It is no longer I who live, but Christ who lives in me. And the life I now live in the flesh I live by faith in the Son of God, who loved me and gave himself for me.'
    },
    scripture2: {
      reference: 'Romans 8:1',
      nlt: 'So now there is no condemnation for those who belong to Christ Jesus.',
      esv: 'There is therefore now no condemnation for those who are in Christ Jesus.'
    },
  },
  3: {
    scripture1: {
      reference: 'John 16:13-14',
      nlt: 'When the Spirit of truth comes, he will guide you into all truth. He will not speak on his own but will tell you what he has heard. He will tell you about the future. He will bring me glory by telling you whatever he receives from me.',
      esv: 'When the Spirit of truth comes, he will guide you into all the truth, for he will not speak on his own authority, but whatever he hears he will speak, and he will declare to you the things that are to come. He will glorify me, for he will take what is mine and declare it to you.'
    },
    scripture2: {
      reference: '1 Corinthians 6:19-20',
      nlt: 'Don\'t you realize that your body is the temple of the Holy Spirit, who lives in you and was given to you by God? You do not belong to yourself, for God bought you with a high price. So you must honor God with your body.',
      esv: 'Or do you not know that your body is a temple of the Holy Spirit within you, whom you have from God? You are not your own, for you were bought with a price. So glorify God in your body.'
    },
  },
  4: {
    scripture1: {
      reference: '1 Corinthians 10:13',
      nlt: 'The temptations in your life are no different from what others experience. And God is faithful. He will not allow the temptation to be more than you can stand. When you are tempted, he will show you a way out so that you can endure.',
      esv: 'No temptation has overtaken you that is not common to man. God is faithful, and he will not let you be tempted beyond your ability, but with the temptation he will also provide the way of escape, that you may be able to endure it.'
    },
    scripture2: {
      reference: '1 Timothy 6:12',
      nlt: 'Fight the good fight for the true faith. Hold tightly to the eternal life to which God has called you, which you have declared so well before many witnesses.',
      esv: 'Fight the good fight of the faith. Take hold of the eternal life to which you were called and about which you made the good confession in the presence of many witnesses.'
    },
  },
  5: {
    scripture1: {
      reference: '2 Corinthians 9:6-7',
      nlt: 'Remember this—a farmer who plants only a few seeds will get a small crop. But the one who plants generously will get a generous crop. You must each decide in your heart how much to give. And don\'t give reluctantly or in response to pressure. "For God loves a person who gives cheerfully."',
      esv: 'The point is this: whoever sows sparingly will also reap sparingly, and whoever sows bountifully will also reap bountifully. Each one must give as he has decided in his heart, not reluctantly or under compulsion, for God loves a cheerful giver.'
    },
    scripture2: {
      reference: 'James 2:15-17',
      nlt: 'Suppose you see a brother or sister who has no food or clothing, and you say, "Good-bye and have a good day; stay warm and eat well"—but then you don\'t give that person any food or clothing. What good does that do? So you see, faith by itself isn\'t enough. Unless it produces good deeds, it is dead and useless.',
      esv: 'If a brother or sister is poorly clothed and lacking in daily food, and one of you says to them, "Go in peace, be warmed and filled," without giving them the things needed for the body, what good is that? So also faith by itself, if it does not have works, is dead.'
    },
  },
  6: {
    scripture1: {
      reference: 'Matthew 5:16',
      nlt: 'In the same way, let your good deeds shine out for all to see, so that everyone will praise your heavenly Father.',
      esv: 'In the same way, let your light shine before others, so that they may see your good works and give glory to your Father who is in heaven.'
    },
    scripture2: {
      reference: 'Matthew 28:19',
      nlt: 'Therefore, go and make disciples of all the nations, baptizing them in the name of the Father and the Son and the Holy Spirit.',
      esv: 'Go therefore and make disciples of all nations, baptizing them in the name of the Father and of the Son and of the Holy Spirit,'
    },
  },
};

const DEFAULT_CONTENT = {
  1: {
    discuss: '<ul><li><strong>God loves you.</strong> He created you for a relationship with Him.</li><li><strong>You are a sinner.</strong> We all have fallen short of God\'s glory.</li><li><strong>Jesus is the savior.</strong> He died for our sins to bring us back to God.</li></ul>',
    live: '<p><strong>This week I will.....</strong></p><p>Write my God story (testimony) and share it with at least one person this week.</p>'
  },
  2: {
    discuss: '<p>You are a new creation in Christ, deeply loved, fully forgiven, totally accepted, and pleasing in His sight.</p>',
    live: '<p><strong>This week I will.....</strong></p><p>Take communion and remember Jesus\' love. If you haven\'t already, set up a time to be baptized.</p>'
  },
  3: {
    discuss: '<p>The Holy Spirit helps you to walk with God daily.</p>',
    live: '<p><strong>This week I will.....</strong></p><p>Fast from one thing to be more open to the Spirit\'s leading.</p>'
  },
  4: {
    discuss: '<p>Christ is your strength in times of struggle.</p>',
    live: '<p><strong>This week I will.....</strong></p><p>Confess and repent to the Lord areas in which I\'m struggling and write a plan of escape. Join a House Church to build a support community.</p>'
  },
  5: {
    discuss: '<p>We model the hope we have in Christ by giving generously.</p>',
    live: '<p><strong>This week I will.....</strong></p><p>Decide an amount to begin giving to the Church.</p>'
  },
  6: {
    discuss: '<p>Your service shows Jesus to others.</p>',
    live: '<p><strong>This week I will.....</strong></p><p>Begin serving by (1) volunteering with a Ministry Partner or (2) becoming a House Church Pastor or Host.</p>'
  },
};

const PRAY_CONTENT = `
  <div class="space-y-4">
    <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Pray Through The Five Elements</h3>
    <div class="space-y-3">
      <div class="flex items-start space-x-3">
        <div class="w-8 h-8 bg-primary bg-opacity-10 dark:bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
          <span class="text-sm font-medium text-primary">1</span>
        </div>
        <div>
          <h4 class="font-medium text-gray-900 dark:text-white">Praise</h4>
          <p class="text-gray-600 dark:text-gray-400 text-sm">Praise God for His character.</p>
        </div>
      </div>
      <div class="flex items-start space-x-3">
        <div class="w-8 h-8 bg-primary bg-opacity-10 dark:bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
          <span class="text-sm font-medium text-primary">2</span>
        </div>
        <div>
          <h4 class="font-medium text-gray-900 dark:text-white">Thanksgiving</h4>
          <p class="text-gray-600 dark:text-gray-400 text-sm">Thank Him for His blessings.</p>
        </div>
      </div>
      <div class="flex items-start space-x-3">
        <div class="w-8 h-8 bg-primary bg-opacity-10 dark:bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
          <span class="text-sm font-medium text-primary">3</span>
        </div>
        <div>
          <h4 class="font-medium text-gray-900 dark:text-white">Confession</h4>
          <p class="text-gray-600 dark:text-gray-400 text-sm">Seek forgiveness for sins.</p>
        </div>
      </div>
      <div class="flex items-start space-x-3">
        <div class="w-8 h-8 bg-primary bg-opacity-10 dark:bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
          <span class="text-sm font-medium text-primary">4</span>
        </div>
        <div>
          <h4 class="font-medium text-gray-900 dark:text-white">Intercession</h4>
          <p class="text-gray-600 dark:text-gray-400 text-sm">Pray for the needs of others.</p>
        </div>
      </div>
      <div class="flex items-start space-x-3">
        <div class="w-8 h-8 bg-primary bg-opacity-10 dark:bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
          <span class="text-sm font-medium text-primary">5</span>
        </div>
        <div>
          <h4 class="font-medium text-gray-900 dark:text-white">Supplication</h4>
          <p class="text-gray-600 dark:text-gray-400 text-sm">Present your personal requests.</p>
        </div>
      </div>
    </div>
  </div>
`;

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

function StatusBadge({ status }) {
  const map = {
    completed: { label: 'Completed', cls: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400', icon: FiCheckCircle },
    in_progress: { label: 'In Progress', cls: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400', icon: FiClock },
    not_started: { label: 'Not Started', cls: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300', icon: FiBook },
  };
  const { label, cls, icon } = map[status] || map.not_started;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${cls}`}>
      <SafeIcon icon={icon} className="w-3.5 h-3.5" />
      {label}
    </span>
  );
}

function ScriptureCard({ scriptureNum, reference, translation, setTranslation, weekNum }) {
  const weekScriptures = SCRIPTURE_TEXTS[weekNum];
  const scripture = weekScriptures?.[scriptureNum];
  const text = scripture ? (translation === 'NLT' ? scripture.nlt : scripture.esv) : '';

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Scripture {scriptureNum === 'scripture1' ? '1' : '2'}
          </label>
          <div className="font-medium text-gray-900 dark:text-white">{reference}</div>
        </div>
        <div className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          {['NLT', 'ESV'].map((t) => (
            <button
              key={t}
              onClick={() => setTranslation(t)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                translation === t
                  ? 'bg-primary text-white'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800/50">
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{text}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-3 flex items-center justify-between">
          <span>— {reference}</span>
          <span className="font-medium">{translation}</span>
        </p>
      </div>
    </div>
  );
}

// ── Shared Notes Panel ────────────────────────────────────────────────────────
function SharedNotesPanel({ pair, weekNumber, userId }) {
  const { note, setNote, loading, saving, saveNote } = useSharedNotes(pair?.id, weekNumber, userId);
  const [localNote, setLocalNote] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');

  // Sync localNote whenever the hook reloads a new week's note
  useEffect(() => {
    setLocalNote(note);
  }, [note]);

  const handleSave = async () => {
    setError('');
    try {
      await saveNote(localNote);
      setSuccessMsg('Notes saved!');
      setTimeout(() => setSuccessMsg(''), 2500);
    } catch (err) {
      setError(err.message || 'Failed to save notes.');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 h-fit sticky top-24 transition-colors"
    >
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-space font-semibold text-gray-900 dark:text-white">Shared Notes</h3>
        <div
          className={`w-3 h-3 rounded-full ${pair ? 'bg-green-400 dark:bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`}
          title={pair ? 'Synced with Supabase' : 'No pair linked'}
        />
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Week {weekNumber} — unique notes per week</p>

      {loading ? (
        <div className="text-center py-8">
          <SafeIcon icon={FiLoader} className="w-5 h-5 text-gray-400 dark:text-gray-500 mx-auto animate-spin" />
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">Loading week {weekNumber} notes…</p>
        </div>
      ) : (
        <>
          <textarea
            value={localNote}
            onChange={(e) => setLocalNote(e.target.value)}
            rows={12}
            className="w-full px-3 py-2 border border-gray-200 dark:border-gray-600 bg-transparent dark:bg-gray-900 dark:text-white rounded-lg focus:ring-1 focus:ring-primary outline-none resize-none text-sm transition-colors"
            placeholder={pair ? `Notes for Week ${weekNumber}…` : 'Link a pair to save notes…'}
            disabled={!pair}
          />

          {pair && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="mt-3 w-full bg-primary text-white py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-60"
            >
              {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <SafeIcon icon={FiSave} className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save Notes'}
            </button>
          )}

          {successMsg && (
            <p className="mt-2 text-center text-xs text-green-600 dark:text-green-400 font-medium">{successMsg}</p>
          )}

          {error && (
            <p className="mt-2 text-center text-xs text-red-500 dark:text-red-400">{error}</p>
          )}
        </>
      )}
    </motion.div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
function WeeklyModule({ userRole }) {
  const { weekNumber } = useParams();
  const currentWeek = parseInt(weekNumber);
  const { user } = useAuth();
  const { pair, loading: pairLoading } = usePair(user?.id, userRole);
  const { getWeekStatus, saveWeek, markComplete } = useModuleProgress(pair?.id);

  const defaults = DEFAULT_CONTENT[currentWeek] || DEFAULT_CONTENT[1];
  const refs = SCRIPTURE_TEXTS[currentWeek];

  const [discuss, setDiscuss] = useState(defaults.discuss);
  const [live, setLive] = useState(defaults.live);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const [s1Translation, setS1Translation] = useState('NLT');
  const [s2Translation, setS2Translation] = useState('NLT');

  const status = getWeekStatus(currentWeek);
  const isCompleted = status === 'completed';
  const noPair = !pairLoading && !pair;

  // Reset content when week changes
  useEffect(() => {
    const d = DEFAULT_CONTENT[currentWeek] || DEFAULT_CONTENT[1];
    setDiscuss(d.discuss);
    setLive(d.live);
    setIsEditing(false);
    setHasChanges(false);
    setError('');
    setSuccessMsg('');
    setS1Translation('NLT');
    setS2Translation('NLT');
  }, [currentWeek]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleSaveContent = async () => {
    if (!pair) return;
    setSaving(true);
    setError('');
    try {
      await saveWeek(currentWeek, {
        discuss_content: discuss,
        live_content: live,
        status: status === 'not_started' ? 'in_progress' : status,
      });
      setHasChanges(false);
      setIsEditing(false);
      showSuccess('Content saved successfully!');
    } catch (err) {
      setError(err.message || 'Failed to save content.');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!pair || isCompleted) return;
    setCompleting(true);
    setError('');
    try {
      await markComplete(currentWeek);
      showSuccess('🎉 Week marked as complete!');
    } catch (err) {
      setError(err.message || 'Failed to mark week as complete.');
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 transition-colors duration-200">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-start justify-between mb-4 flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            {currentWeek > 1 && (
              <Link
                to={`/week/${currentWeek - 1}`}
                className="p-2 text-gray-400 dark:text-gray-500 hover:text-primary dark:hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
              </Link>
            )}
            <div>
              <h1 className="text-3xl font-space font-bold text-gray-900 dark:text-white">
                Week {currentWeek}: {WEEK_TITLES[currentWeek]}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <StatusBadge status={status} />
                {noPair && (
                  <span className="text-xs text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800/50 px-2 py-1 rounded-full">
                    No pair linked — progress won't be saved
                  </span>
                )}
              </div>
            </div>
            {currentWeek < 6 && (
              <Link
                to={`/week/${currentWeek + 1}`}
                className="p-2 text-gray-400 dark:text-gray-500 hover:text-primary dark:hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
              </Link>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 flex-wrap">
            {userRole === 'leader' && pair && (
              <>
                {isEditing && hasChanges && (
                  <button
                    onClick={handleSaveContent}
                    disabled={saving}
                    className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2 disabled:opacity-60"
                  >
                    {saving ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <SafeIcon icon={FiSave} className="w-4 h-4" />}
                    <span>{saving ? 'Saving…' : 'Save'}</span>
                  </button>
                )}
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    isEditing
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <SafeIcon icon={isEditing ? FiCheck : FiEdit3} className="w-4 h-4" />
                  <span>{isEditing ? 'Done' : 'Edit'}</span>
                </button>
              </>
            )}

            {pair && !isCompleted && (
              <button
                onClick={handleMarkComplete}
                disabled={completing}
                className="bg-green-600 dark:bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-700 dark:hover:bg-green-600 transition-colors flex items-center gap-2 disabled:opacity-60 font-medium"
              >
                {completing ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />}
                <span>{completing ? 'Saving…' : 'Mark Complete'}</span>
              </button>
            )}

            {isCompleted && (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg text-green-700 dark:text-green-400 font-medium transition-colors">
                <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />
                <span>Week Completed!</span>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/50 rounded-lg text-sm text-red-600 dark:text-red-400 transition-colors">
            <SafeIcon icon={FiAlertCircle} className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {successMsg && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/50 rounded-lg text-sm text-green-700 dark:text-green-400 transition-colors"
          >
            <SafeIcon icon={FiCheckCircle} className="w-4 h-4 flex-shrink-0" />
            {successMsg}
          </motion.div>
        )}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {/* READ */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiBook} className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-xl font-space font-semibold text-gray-900 dark:text-white">READ</h2>
            </div>

            <div className="space-y-6">
              <ScriptureCard
                scriptureNum="scripture1"
                reference={refs?.scripture1?.reference || ''}
                translation={s1Translation}
                setTranslation={setS1Translation}
                weekNum={currentWeek}
              />
              <ScriptureCard
                scriptureNum="scripture2"
                reference={refs?.scripture2?.reference || ''}
                translation={s2Translation}
                setTranslation={setS2Translation}
                weekNum={currentWeek}
              />
            </div>
          </motion.div>

          {/* DISCUSS */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiMessageCircle} className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-xl font-space font-semibold text-gray-900 dark:text-white">DISCUSS</h2>
            </div>
            {isEditing && userRole === 'leader' ? (
              <ReactQuill
                theme="snow"
                value={discuss}
                onChange={(v) => {
                  setDiscuss(v);
                  setHasChanges(true);
                }}
                modules={quillModules}
              />
            ) : (
              <div className="prose prose-gray max-w-none dark:text-gray-300" dangerouslySetInnerHTML={{ __html: discuss }} />
            )}
          </motion.div>

          {/* PRAY */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiHeart} className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h2 className="text-xl font-space font-semibold text-gray-900 dark:text-white">PRAY</h2>
            </div>
            <div dangerouslySetInnerHTML={{ __html: PRAY_CONTENT }} />
          </motion.div>

          {/* LIVE */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 transition-colors"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiTarget} className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <h2 className="text-xl font-space font-semibold text-gray-900 dark:text-white">LIVE</h2>
            </div>
            {isEditing && userRole === 'leader' ? (
              <ReactQuill
                theme="snow"
                value={live}
                onChange={(v) => {
                  setLive(v);
                  setHasChanges(true);
                }}
                modules={quillModules}
              />
            ) : (
              <div className="prose prose-gray max-w-none dark:text-gray-300" dangerouslySetInnerHTML={{ __html: live }} />
            )}
          </motion.div>
        </div>

        {/* Shared Notes — isolated component so it re-mounts per week */}
        <SharedNotesPanel
          key={`notes-${pair?.id}-${currentWeek}`}
          pair={pair}
          weekNumber={currentWeek}
          userId={user?.id}
        />
      </div>
    </div>
  );
}

export default WeeklyModule;